import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, ChevronDown, ChevronRight } from "lucide-react";

const pct = (v, d = 1) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(d)}%`);
const tone = (v) => (v > 0 ? "text-emerald-600" : v < 0 ? "text-red-600" : "text-neutral-600");
const mcap = (v) => (v >= 1e12 ? `$${(v / 1e12).toFixed(2)}T` : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(0)}M`);

export const Ticker = ({ sym }) => (
  <Link to={`/app/stock/${sym}`} className="font-mono-data font-semibold text-mb-violet hover:underline" data-testid={`uxbt-ticker-${sym}`}>
    {sym}
  </Link>
);

export function Section({ title, sub, children, testId }) {
  return (
    <section className="mt-8 bg-white border border-mb-border rounded-lg overflow-hidden" data-testid={testId}>
      <div className="px-6 py-4 border-b border-mb-border">
        <h2 className="text-base md:text-lg font-semibold text-mb-ink">{title}</h2>
        {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

export function VerdictBanner({ verdict, current, expanded }) {
  const Icon = verdict.pass ? CheckCircle2 : XCircle;
  return (
    <div className={`mt-8 rounded-lg border-2 p-6 ${verdict.pass ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10"}`} data-testid="uxbt-verdict">
      <div className="flex items-center gap-3">
        <Icon className={`w-8 h-8 ${verdict.pass ? "text-emerald-500" : "text-red-500"}`} />
        <span className={`text-4xl sm:text-5xl font-bold tracking-tight ${verdict.pass ? "text-emerald-500" : "text-red-500"}`} data-testid="uxbt-verdict-label">
          {verdict.label}
        </span>
      </div>
      <p className="mt-3 text-sm text-neutral-700">{verdict.rule}</p>
      {verdict.driver && <p className="mt-2 text-sm text-mb-ink font-medium" data-testid="uxbt-verdict-driver">{verdict.driver}</p>}
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <span data-testid="uxbt-verdict-profit">Total profit: <b>{pct(current.total_profit_pct)}</b> → <b className={tone(verdict.profit_delta_pct)}>{pct(expanded.total_profit_pct)}</b> ({pct(verdict.profit_delta_pct)})</span>
        <span data-testid="uxbt-verdict-winrate">Win rate: <b>{current.win_rate}%</b> → <b className={tone(verdict.win_rate_delta_pp)}>{expanded.win_rate}%</b> ({verdict.win_rate_delta_pp > 0 ? "+" : ""}{verdict.win_rate_delta_pp}pp)</span>
      </div>
    </div>
  );
}

const STAT_ROWS = [
  ["alerts", "Alerts", (v) => v],
  ["open", "Still open (marked to last close)", (v) => v],
  ["win_rate", "Win rate (closed)", (v) => `${v}%`],
  ["avg_return_pct", "Avg return per closed alert", (v) => pct(v, 2)],
  ["total_profit_pct", "Total book profit (sum of closed returns)", (v) => pct(v)],
  ["stop_rate", "Stop-out rate", (v) => `${v}%`],
  ["moon_rate", "Moon rate (touched +20%)", (v) => `${v}%`],
  ["max_drawdown_pct", "Worst drawdown (cumulative P&L)", (v) => `${v}%`],
];

export function StatsTable({ current, expanded, testId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
            <th className="py-2 pr-4 font-medium">Metric</th>
            <th className="py-2 pr-4 font-medium text-right">Current book</th>
            <th className="py-2 font-medium text-right">Expanded book</th>
          </tr>
        </thead>
        <tbody>
          {STAT_ROWS.map(([k, label, fmt]) => (
            <tr key={k} className="border-t border-mb-border">
              <td className="py-2 pr-4 text-neutral-700">{label}</td>
              <td className="py-2 pr-4 text-right font-mono-data" data-testid={`${testId}-current-${k}`}>{fmt(current[k])}</td>
              <td className="py-2 text-right font-mono-data" data-testid={`${testId}-expanded-${k}`}>{fmt(expanded[k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BandTable({ bands }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[560px]" data-testid="uxbt-bands-table">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
            <th className="py-2 pr-4 font-medium">Size band</th>
            <th className="py-2 pr-4 font-medium text-right">Alerts (cur → exp)</th>
            <th className="py-2 pr-4 font-medium text-right">Win rate (exp)</th>
            <th className="py-2 pr-4 font-medium text-right">Avg (exp)</th>
            <th className="py-2 font-medium text-right">Total profit (cur → exp)</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(bands).map(([label, b]) => (
            <tr key={label} className="border-t border-mb-border" data-testid={`uxbt-band-${label}`}>
              <td className="py-2 pr-4 font-semibold text-mb-ink">{label}</td>
              <td className="py-2 pr-4 text-right font-mono-data">{b.current.alerts} → {b.expanded.alerts}</td>
              <td className="py-2 pr-4 text-right font-mono-data">{b.expanded.closed ? `${b.expanded.win_rate}%` : "—"}</td>
              <td className={`py-2 pr-4 text-right font-mono-data ${tone(b.expanded.avg_return_pct)}`}>{b.expanded.closed ? pct(b.expanded.avg_return_pct, 2) : "—"}</td>
              <td className="py-2 text-right font-mono-data">{pct(b.current.total_profit_pct)} → <span className={tone(b.expanded.total_profit_pct - b.current.total_profit_pct)}>{pct(b.expanded.total_profit_pct)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AlertList({ rows, testId, empty }) {
  const [open, setOpen] = useState(false);
  const shown = open ? rows : rows.slice(0, 12);
  if (!rows.length) return <p className="text-sm text-neutral-500">{empty}</p>;
  const total = rows.filter((r) => r.status !== "ACTIVE").reduce((s, r) => s + r.return_pct, 0);
  return (
    <div data-testid={testId}>
      <div className="text-xs text-neutral-500 mb-2">{rows.length} alerts · closed ones sum to <b className={tone(total)}>{pct(total)}</b></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
              <th className="py-1.5 pr-3 font-medium">Ticker</th><th className="py-1.5 pr-3 font-medium">Entry</th>
              <th className="py-1.5 pr-3 font-medium">Size</th><th className="py-1.5 pr-3 font-medium text-right">Conf</th>
              <th className="py-1.5 pr-3 font-medium">Outcome</th><th className="py-1.5 font-medium text-right">Return</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={`${r.symbol}-${r.entry_date}`} className="border-t border-mb-border">
                <td className="py-1.5 pr-3"><Ticker sym={r.symbol} /></td>
                <td className="py-1.5 pr-3 font-mono-data text-neutral-700">{r.entry_date} @ ${r.entry_price}</td>
                <td className="py-1.5 pr-3 text-neutral-600">{mcap(r.market_cap)}</td>
                <td className="py-1.5 pr-3 text-right font-mono-data">{Math.round(r.confidence * 100)}%</td>
                <td className="py-1.5 pr-3 text-xs text-neutral-600">{r.status.replace("_", " ")}</td>
                <td className={`py-1.5 text-right font-mono-data ${tone(r.return_pct)}`}>{pct(r.return_pct, 2)}{r.status === "ACTIVE" ? "*" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 12 && (
        <button onClick={() => setOpen(!open)} className="mt-3 text-xs font-semibold text-mb-violet inline-flex items-center gap-1" data-testid={`${testId}-toggle`}>
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {open ? "Show fewer" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

export { pct, tone, mcap };
